import { createTheme } from '@mui/material/styles'
import { glassCard, glassCardSmall, glassInfoBox, glass, createGlassOverlay, glassToggleControl } from './glassEffects'
import { colors as genColors } from './BeoflowColors.generated'
import { textStyles as genText, fontFamilies as genFamilies } from './BeoflowTypography.generated'
import { spacing as genSpacing, cornerRadius as genRadius, layout as genLayout } from './BeoflowSpacingLayout.generated'
import { buttonSpec } from './BeoflowButtonSpec.generated'
import { cardSpec } from './BeoflowCardSpec.generated'
import { chipSpec } from './BeoflowChipSpec.generated'
import { textFieldSpec } from './BeoflowTextFieldSpec.generated'
import { zoneColorByIndex } from './BeoflowZoneColors.generated'

// px() turns a numeric design token (e.g. 4) into the CSS px string ('4px') the webapp uses.
const px = (n: number): string => `${n}px`

// ============================================================================
// BEOFLOW WEBAPP MATERIAL-UI THEME
// ============================================================================
//
// The token objects below (beoflowColors / typography / spacing / borderRadius /
// layout) are SOURCED FROM THE GENERATED DESIGN TOKENS — `./BeoflowColors.generated`,
// `./BeoflowTypography.generated`, `./BeoflowSpacingLayout.generated`, emitted from the
// Drafted "Beoflow Design System" foundation frames. Drafted is the single source of
// truth; to change a value, edit the Drafted frame and re-run `npm run tokens:gen`,
// NOT this file.
//
// Deliberate webapp overrides (do not match the token, by design):
//   - borderRadius.button: pill (9999px) — token cornerRadius.button is 8
//   - layout.inputFontSize: 16px — ≥16px prevents mobile-Safari auto-zoom (token is 14)
// Web-only (no token): beoflowColors.zoneColors (HR-zone ramp), shadows (hard shadows),
//   the MUI-compat rem spacing keys, line-heights, and breakpoints.
//
// Brutalist principles (maintained): pill buttons · uppercase mono for buttons/data ·
// hard shadows · 2px borders · high contrast · no ripple (disableRipple: true).
//
// ============================================================================

// ============================================================================
// BEOFLOW BRAND COLORS
// ============================================================================
// Palette — sourced from the generated tokens (BeoflowColors.generated.ts) so Drafted
// is the single source of truth. Only the web-only HR-zone ramp stays hand-maintained.
export const beoflowColors = {
  // Primary
  peakOrange: genColors.peakOrange,
  meanBlue: genColors.meanBlue,
  beoWhite: genColors.beoWhite,
  beoBlack: genColors.beoBlack,          // token #1E1E1E (was web-only #000000 — conformed)
  beoBlacker: genColors.beoBlacker,
  pimpinPurple: genColors.pimpinPurple,
  yoloYellow: genColors.yoloYellow,
  biancchiTeal: genColors.biancchiTeal,
  graphGreyDark: genColors.graphGreyDark,
  graphGreyLight: genColors.graphGreyLight,

  // Tints
  orangeLight: genColors.orangeLight,
  orangeBase: genColors.orangeBase,
  orangeDark: genColors.orangeDark,
  blueLight: genColors.blueLight,
  blueBase: genColors.blueBase,
  blueDark: genColors.blueDark,
  purpleLight: genColors.purpleLight,
  purpleBase: genColors.purpleBase,
  purpleDark: genColors.purpleDark,
  yellowLight: genColors.yellowLight,
  yellowBase: genColors.yellowBase,
  yellowDark: genColors.yellowDark,
  tealLight: genColors.tealLight,
  tealBase: genColors.tealBase,
  tealDark: genColors.tealDark,
  redLight: genColors.redLight,
  redBase: genColors.redBase,
  redDark: genColors.redDark,

  // UI background — token (was #252525 — conformed to #121212)
  beoBackground: genColors.beoBackground,

  // Exercise type accents — generated aliases
  exerciseStrengthColor: genColors.exerciseStrengthColor,
  exerciseFunctionalColor: genColors.exerciseFunctionalColor,
  exerciseCardioColor: genColors.exerciseCardioColor,
  exerciseRecoveryColor: genColors.exerciseRecoveryColor,
  exerciseBodyweightColor: genColors.exerciseBodyweightColor,

  // Zone Colors (0-7) — webapp HR-zone ramp. Generated from Drafted (Foundations / Zone Colors),
  // extracted from iOS Color+HRZone. The token `zoneRamp` is a separate opacity ladder.
  zoneColors: zoneColorByIndex,

}

// ============================================================================
// SHADOWS - BRUTALIST HARD SHADOWS
// No soft shadows with blur - only hard offset shadows using beoBlacker (#121212)
// ============================================================================
export const shadows = {
  none: 'none',
  // Soft, subtle elevation for overlays/cards. The prior values were iOS-style
  // hard offset shadows ('Npx Npx 0 #121212') — a neo-brutalist look that was
  // never part of the web design system. Removed.
  small: '0 1px 3px rgba(18, 18, 18, 0.12)',
  medium: '0 4px 12px rgba(18, 18, 18, 0.16)',
  large: '0 12px 32px rgba(18, 18, 18, 0.22)',
  pressed: 'none',
}

// ============================================================================
// ZONE COLORS — Semantic status zones (synced with iOS)
// ============================================================================
export const zones = {
  good: beoflowColors.biancchiTeal,     // Recovery, success
  neutral: beoflowColors.meanBlue,      // Normal, baseline
  caution: beoflowColors.yoloYellow,    // Warning, approaching limit
  attention: beoflowColors.peakOrange,  // Action needed, high priority
}

// ============================================================================
// OPACITY TIERS — Hex suffixes for rgba-via-hex patterns
// ============================================================================
export const opacity = {
  footer: '08',       // 3% — footers, ultra-subtle backgrounds
  glass: '0D',        // 5% — glass tint
  interactive: '1A',  // 10% — hover states, interactive backgrounds
  border: '26',       // 15% — glass borders
  separator: '15',    // 8% — list separators
  disabled: '80',     // 50% — disabled elements
  // 70% — secondary text. Was 60%, which passed WCAG (6.4:1 on the navbar's
  // own surface) but read as dim grey-on-grey rather than as quiet text:
  // labels, units and placeholders all sit on this one token, so the whole
  // second tier of type was a step too faint. 70% takes the navbar's sport row
  // to 8.2:1 and keeps it clearly subordinate to primary.
  secondary: 'B3',
}

// ============================================================================
// SPACING SCALE
// ============================================================================
export const spacing = {
  0: 0,
  // Scale — generated tokens (4px base unit)
  xs: px(genSpacing.xs),
  sm: px(genSpacing.sm),
  md: px(genSpacing.md),
  lg: px(genSpacing.lg),
  xl: px(genSpacing.xl),
  xxl: px(genSpacing.xxl),
  xxxl: px(genSpacing.xxxl),
  xxxxl: px(genSpacing.xxxxl),
  xxxxxl: px(genSpacing.xxxxxl),
  xxxxxxl: px(genSpacing.xxxxxxl),
  xxxxxxxl: px(genSpacing.xxxxxxxl),

  // Semantic spacing — generated tokens (conformed to iOS: componentPadding 12, cardPadding 20,
  // sectionSpacing/screenPadding 36, elementSpacing 8, tightSpacing 4, looseSpacing 44, gridGap 20)
  componentPadding: px(genSpacing.componentPadding),
  cardPadding: px(genSpacing.cardPadding),
  sectionSpacing: px(genSpacing.sectionSpacing),
  screenPadding: px(genSpacing.screenPadding),
  elementSpacing: px(genSpacing.elementSpacing),
  tightSpacing: px(genSpacing.tightSpacing),
  looseSpacing: px(genSpacing.looseSpacing),
  gridGap: px(genSpacing.gridGap),

  // MUI compatibility rem scale (webapp-only, not a design token)
  1: '0.25rem',   // 4px
  2: '0.5rem',    // 8px
  3: '1rem',      // 16px (base spacer)
  4: '1.5rem',    // 24px
  5: '3rem',      // 48px
  6: '4rem',      // 64px
  7: '5rem',      // 80px
  8: '6rem',      // 96px
  9: '8rem',      // 128px
  10: '10rem',    // 160px
  11: '12rem',    // 192px
  12: '14rem',    // 224px
}

// ============================================================================
// BORDER RADIUS - BRUTALIST CONSISTENT RADII
// ============================================================================
export const borderRadius = {
  none: '0',
  // Generated tokens (cornerRadius)
  small: px(genRadius.small),
  medium: px(genRadius.medium),
  large: px(genRadius.large),
  xlarge: px(genRadius.xlarge),
  round: px(genRadius.round),   // 9999px

  // Semantic radii — generated, except `button`
  button: '9999px',             // WEB OVERRIDE: pill buttons on web (token cornerRadius.button is 8)
  card: px(genRadius.card),
  sheet: px(genRadius.sheet),
  input: px(genRadius.input),

}

// ============================================================================
// TYPOGRAPHY SCALE - BRUTALIST
// Raleway for headings/body, IBM Plex Mono for data/buttons/inputs
// ============================================================================
export const typography = {
  // Font Families — primary family sourced from the generated tokens; web fallback stacks kept
  fontFamilySansSerif: `"${genFamilies.raleway}", Helvetica, Arial, sans-serif`,
  fontFamilyMonospace: `"${genFamilies.ibmPlexMono}", "SFMono-Regular", Menlo, Monaco, Consolas, monospace`,
  // Orbitron display face for nav titles / main-view headers. genFamilies.orbitron is emitted
  // web-idiomatic ("Orbitron"); the Black weight is applied numerically in the navTitle variant.
  fontFamilyDisplay: `"${genFamilies.orbitron}", "${genFamilies.raleway}", sans-serif`,
  // The generated metric family is already a CSS generic, so it needs no stack.
  fontFamilyMetric: genFamilies.system,

  // Font Sizes — generated where a token style exists; generic compat sizes kept
  fontSizeTiny: px(genText.bodyTiny.fontSize),    // 10 (was 11 — conformed)
  fontSizeXs: px(genText.bodySmall.fontSize),     // 12 (was 13 — conformed)
  fontSizeSm: px(genText.body.fontSize),          // 14
  fontSizeLg: px(genText.heading3.fontSize),      // 16
  fontSizeXl: px(genText.heading2.fontSize),      // 20
  fontSize2xl: px(genText.heading1.fontSize),     // 24
  fontSize3xl: '30px',       // compat (no token)
  fontSize4xl: '32px',       // compat (no token)
  fontSize5xl: px(genText.metricLarge.fontSize),  // 36
  fontSize6xl: '48px',       // compat
  fontSize7xl: '60px',       // compat
  fontSize8xl: '64px',       // compat
  fontSize9xl: '80px',       // compat

  // Heading Sizes — conformed to the generated heading scale (was 32/24/20/16/14/13)
  h1FontSize: px(genText.heading1.fontSize),      // 24
  h2FontSize: px(genText.heading2.fontSize),      // 20
  h3FontSize: px(genText.heading3.fontSize),      // 16
  h4FontSize: px(genText.heading4.fontSize),      // 14
  h5FontSize: px(genText.body.fontSize),          // 14
  h6FontSize: px(genText.bodySmall.fontSize),     // 12 (was 13 — conformed)

  // Card Typography — generated
  cardSportLabel: px(genText.cardSportLabel.fontSize),    // 8
  cardTitle: px(genText.cardTitle.fontSize),              // 16
  cardDescription: px(genText.cardDescription.fontSize),  // 12

  // Metric Typography — generated
  metricLarge: px(genText.metricLarge.fontSize),          // 36

  // Font Weights (standard CSS weights)
  fontWeightLight: 300,
  fontWeightNormal: 400,      // iOS: regular
  fontWeightMedium: 500,      // iOS: medium
  fontWeightSemibold: 600,    // iOS: semiBold
  fontWeightBold: 700,        // iOS: bold

  // Line Heights - Brutalist (iOS: font size + 8px)
  lineHeightBase: 1.6,        // ~8px additional at 14px
  lineHeightSm: 1.25,
  lineHeightLg: 2,
  h1LineHeight: 1.2,
  h2LineHeight: 1.2,
  h3LineHeight: 1.2,
  h4LineHeight: 1.2,
  h5LineHeight: 1.2,
  h6LineHeight: 1.3,
  pLineHeight: 1.6,
  leadLineHeight: 1.3,

  // Letter Spacing - Brutalist (matching iOS)
  letterNormal: '0px',        // iOS: default
  letterButton: '0.5px',      // iOS: buttons - 0.5px
  letterLabel: '1px',         // iOS: labels - 1px (uppercase)
  h1LetterSpacing: '0px',
  aLetterSpacing: '0px',
}

/**
 * The web fallback stack for a font family taken from the generated tokens.
 *
 * `BeoflowTypography.generated` emits `fontFamily` as the RAW family name,
 * "Raleway" and not a stack, because it mirrors iOS where a family either is
 * installed or the build is broken. On the web that same value is a live
 * failure mode: our faces come from Google Fonts over the network, and CSS
 * given one unavailable family falls back to the browser DEFAULT, which is a
 * SERIF. So a component that passes a raw token family straight into `sx` does
 * not degrade to Arial like the rest of the app, it degrades to Times, and it
 * does so whenever the webfont is slow, blocked or offline.
 *
 * Measured 2026-09-18 on the coach wall: with fonts.gstatic.com blocked, every
 * themed surface fell back to Arial and stayed legible, while PlanChoice's plan
 * names rendered in serif and the screen read as broken.
 *
 * Use this at any site that reads `textStyles.<x>.fontFamily`.
 */
export function fontStackFor(generatedFamily: string): string {
  switch (generatedFamily) {
    case genFamilies.raleway: return typography.fontFamilySansSerif
    case genFamilies.ibmPlexMono: return typography.fontFamilyMonospace
    case genFamilies.orbitron: return typography.fontFamilyDisplay
    // system-ui is a CSS generic and always resolves; anything unrecognised is
    // safer with the body stack behind it than on its own.
    case genFamilies.system: return typography.fontFamilyMetric
    default: return `"${generatedFamily}", ${typography.fontFamilySansSerif}`
  }
}

// ============================================================================
// LAYOUT CONSTANTS
// ============================================================================
export const layout = {
  // Component sizes — generated tokens
  buttonHeight: px(genLayout.buttonHeight),
  inputHeight: px(genLayout.inputHeight),
  toolbarHeight: px(genLayout.toolbarHeight),
  tabBarHeight: px(genLayout.tabBarHeight),
  navigationBarHeight: px(genLayout.navigationBarHeight),
  // Webapp-only navbar height in px (used in calc()); not a token. Matches the
  // event panel's own header bar, so a panel opened beside the page reads as
  // one band rather than two bars of different heights.
  navbarHeight: 56,
  // Height of a control sitting in the navbar's action slot. The athlete
  // selector has carried this as a literal since it was written; anything else
  // docked beside it has to agree or the row stops reading as one band.
  navbarActionHeight: 34,

  // Border widths — generated
  borderDefault: px(genLayout.borderDefault),
  borderThin: px(genLayout.borderThin),
  borderThick: px(genLayout.borderThick),

  // Input & Selector Standards — generated
  inputCornerRadius: px(genLayout.inputCornerRadius),
  inputHorizontalPadding: px(genLayout.inputHorizontalPadding),
  inputVerticalPadding: px(genLayout.inputVerticalPadding),
  inputFontSize: '16px',        // WEB OVERRIDE: ≥16px prevents mobile Safari auto-zoom (token is 14)

  // Card Standards — generated
  cardAccentStripWidth: px(genLayout.cardAccentStripWidth),
  cardBorderWidth: px(genLayout.cardBorderWidth),
  cardPadding: px(genLayout.cardPadding),
  cardTitleFontSize: px(genLayout.cardTitleFontSize),
  cardSportLabelFontSize: px(genLayout.cardSportLabelFontSize),
  cardDescriptionFontSize: px(genLayout.cardDescriptionFontSize),

  // Icon sizes — generated
  iconTiny: px(genLayout.iconTiny),
  iconSmall: px(genLayout.iconSmall),
  iconMedium: px(genLayout.iconMedium),
  iconLarge: px(genLayout.iconLarge),
  iconXLarge: px(genLayout.iconXLarge),

  // Chat UI (webapp-specific; not tokens)
  chatAvatarSize: 36,
  chatSendButtonSize: 36,
  chatBubblePadding: '10px 14px',
  chatInputPadding: '4px 8px 4px 14px',

  // Max content width — generated
  maxContentWidth: px(genLayout.maxContentWidth),
}

// ============================================================================
// BREAKPOINTS
// From material-dashboard/_variables.scss
// ============================================================================
export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
}

// ============================================================================
// MUI THEME CONFIGURATION
// ============================================================================

// Shared config that doesn't change between modes
const sharedTypography = {
  fontFamily: typography.fontFamilySansSerif,
  fontSize: 16,
  fontWeightLight: typography.fontWeightLight,
  fontWeightRegular: typography.fontWeightNormal,
  fontWeightMedium: typography.fontWeightMedium,
  fontWeightBold: typography.fontWeightBold,

  h1: {
    fontSize: typography.h1FontSize,
    fontWeight: typography.fontWeightBold,
    lineHeight: typography.h1LineHeight,
    letterSpacing: typography.letterNormal,
  },
  h2: {
    fontSize: typography.h2FontSize,
    fontWeight: typography.fontWeightBold,
    lineHeight: typography.h2LineHeight,
    letterSpacing: typography.letterNormal,
  },
  h3: {
    fontSize: typography.h3FontSize,
    fontWeight: typography.fontWeightBold,
    lineHeight: typography.h3LineHeight,
    letterSpacing: typography.letterNormal,
  },
  h4: {
    fontSize: typography.h4FontSize,
    fontWeight: typography.fontWeightSemibold,
    lineHeight: typography.h4LineHeight,
    letterSpacing: typography.letterNormal,
  },
  h5: {
    fontSize: typography.h5FontSize,
    fontWeight: typography.fontWeightSemibold,
    lineHeight: typography.h5LineHeight,
  },
  h6: {
    fontSize: typography.h6FontSize,
    fontWeight: typography.fontWeightSemibold,
    lineHeight: typography.h6LineHeight,
  },
  subtitle1: {
    fontSize: typography.fontSizeLg,
    fontWeight: typography.fontWeightNormal,
    lineHeight: typography.lineHeightBase,
  },
  subtitle2: {
    fontSize: typography.fontSizeSm,
    fontWeight: typography.fontWeightNormal,
    lineHeight: typography.lineHeightBase,
  },
  body1: {
    fontSize: typography.fontSizeSm,
    fontWeight: typography.fontWeightNormal,
    lineHeight: 1.625,
    letterSpacing: typography.letterNormal,
  },
  body2: {
    fontSize: typography.fontSizeXs,
    fontWeight: typography.fontWeightNormal,
    lineHeight: typography.lineHeightSm,
  },
  button: {
    fontFamily: typography.fontFamilyMonospace,
    fontSize: typography.fontSizeSm,
    fontWeight: typography.fontWeightMedium,
    lineHeight: 1.6,
    textTransform: 'uppercase' as const,
    letterSpacing: typography.letterButton,
  },
  caption: {
    fontSize: typography.fontSizeXs,
    fontWeight: typography.fontWeightNormal,
    lineHeight: typography.lineHeightSm,
  },
  overline: {
    fontSize: typography.fontSizeTiny,
    fontWeight: typography.fontWeightSemibold,
    lineHeight: 1.2,
    textTransform: 'uppercase' as const,
    letterSpacing: typography.letterLabel,
  },
  monoSmall: {
    fontFamily: typography.fontFamilyMonospace,
    fontSize: '11px',
    fontWeight: typography.fontWeightNormal,
    textTransform: 'uppercase' as const,
    letterSpacing: '1px',
    lineHeight: 1.2,
  },
  monoBase: {
    fontFamily: typography.fontFamilyMonospace,
    fontSize: '14px',
    fontWeight: typography.fontWeightMedium,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    lineHeight: 1.6,
  },
  monoMetric: {
    fontFamily: typography.fontFamilyMonospace,
    fontSize: '36px',
    fontWeight: typography.fontWeightBold,
    textTransform: 'uppercase' as const,
    letterSpacing: '-0.7px',
    lineHeight: 1.1,
  },
  monoLabel: {
    fontFamily: typography.fontFamilyMonospace,
    fontSize: '11px',
    fontWeight: typography.fontWeightSemibold,
    textTransform: 'uppercase' as const,
    letterSpacing: '1px',
    lineHeight: 1.2,
  },
  // Nav title / main-view header — Orbitron-Black uppercase (design-system navTitle token, 16px).
  navTitle: {
    fontFamily: typography.fontFamilyDisplay,
    fontSize: '16px',
    fontWeight: 900,
    textTransform: 'uppercase' as const,
    letterSpacing: '-0.5px',
    lineHeight: 1.1,
  },
  // Generated tokens (genText.dataUnit / genText.bodyTiny) with no prior MUI variant --
  // dataUnit is the mono unit suffix on a metric value (W, bpm, km/h); bodyTiny is small
  // Raleway body copy (below `caption`'s 12px, not uppercase like `overline`).
  dataUnit: {
    fontFamily: typography.fontFamilyMonospace,
    fontSize: px(genText.dataUnit.fontSize),
    fontWeight: typography.fontWeightNormal,
    textTransform: 'uppercase' as const,
    lineHeight: 1.2,
  },
  bodyTiny: {
    fontSize: typography.fontSizeTiny,
    fontWeight: typography.fontWeightNormal,
    lineHeight: 1.4,
  },
  // Two more generated tokens with no prior MUI variant. sectionHeader is the
  // canonical column / section label (mono SemiBold 12, tracking 1); dataHighlight
  // is the canonical metric value, and it is the SYSTEM face, never Raleway.
  sectionHeader: {
    fontFamily: typography.fontFamilyMonospace,
    fontSize: px(genText.sectionHeader.fontSize),
    fontWeight: genText.sectionHeader.fontWeight,
    letterSpacing: px(genText.sectionHeader.letterSpacing),
    textTransform: 'uppercase' as const,
    lineHeight: 1.2,
  },
  dataHighlight: {
    fontFamily: typography.fontFamilyMetric,
    fontSize: px(genText.dataHighlight.fontSize),
    fontWeight: genText.dataHighlight.fontWeight,
    lineHeight: px(genText.dataHighlight.lineHeight),
    letterSpacing: px(genText.dataHighlight.letterSpacing),
    fontVariantNumeric: 'tabular-nums',
  },
}

const sharedShape = {
  borderRadius: parseFloat(borderRadius.medium),
}

const sharedBreakpoints = {
  values: {
    xs: breakpoints.xs,
    sm: breakpoints.sm,
    md: breakpoints.md,
    lg: breakpoints.lg,
    xl: breakpoints.xl,
  },
}

const sharedShadows: ['none', string, string, string, string, string, string, string, string, string, string, string, string, string, string, string, string, string, string, string, string, string, string, string, string] = [
  'none',
  'none',
  'none',
  'none',
  shadows.small,
  shadows.small,
  shadows.medium,
  shadows.medium,
  shadows.medium,
  shadows.medium,
  shadows.large,
  shadows.large,
  shadows.large,
  shadows.large,
  shadows.large,
  shadows.large,
  shadows.large,
  shadows.large,
  shadows.large,
  shadows.large,
  shadows.large,
  shadows.large,
  shadows.large,
  shadows.large,
  shadows.large,
]

// ============================================================================
// MENU SURFACE — the ONE definition of what a dropdown looks like.
//
// Every ellipsis menu, overflow menu, athlete picker and Select dropdown in the
// app renders a MuiMenu or MuiPopover paper, and they must all look the same.
// Change this function; do not restyle a paper at the call site.
//
// Two things this deliberately does NOT do:
//
// 1. NO GLASS. Dropdowns used to inherit createGlassOverlay(glass.ultraThin),
//    which made the menu translucent and blurred whatever sat behind it. Menus
//    carry short, dense, high-value text -- a filename, an athlete's name, a
//    destructive action -- and putting a calendar grid behind it at 60% is a
//    legibility cost with no upside. Glass stays on sustained chrome (navbar,
//    sidebar, toolbars), not on transient surfaces you read and dismiss. The
//    explicit `backdropFilter: 'none'` matters: menus opened from the navbar
//    render inside its dark sub-theme (Navbar.tsx wraps itself in
//    createBeoflowTheme('dark') while the app is light), so anything short of
//    an explicit reset let the glass back in through that path.
//
// 2. NO PER-CALL RADIUS. Seven call sites had each re-inlined
//    `borderRadius: borderRadius.large` because the old default (small) was
//    wrong. That is what inlining looks like when the centre is wrong -- fix
//    the centre. Call sites now pass only what is genuinely local to them:
//    minWidth, maxHeight, width.
// ============================================================================
export function menuSurface(mode: 'light' | 'dark') {
  const isLight = mode === 'light'
  return {
    backgroundColor: isLight ? '#FFFFFF' : beoflowColors.beoBlack,
    border: `1px solid ${isLight ? '#E0E0E0' : `${beoflowColors.beoWhite}${opacity.separator}`}`,
    borderRadius: borderRadius.large,
    boxShadow: shadows.large,
    // MUI paints an elevation gradient over dark-mode Paper; without this the
    // surface is not the colour the token says it is.
    backgroundImage: 'none',
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
  }
}

/** A row inside that surface.
 *
 *  Geometry is identical in both modes; only the two highlight colours move.
 *  Dark mode used to inverse-video on hover -- solid beoWhite background, black
 *  text -- while light mode used a quiet #F5F5F5 tint. Hovering the SAME menu
 *  therefore looked like two different components depending on which theme
 *  subtree it happened to render in. Both are now a tint of the foreground. */
export function menuItemStyle(mode: 'light' | 'dark') {
  const isLight = mode === 'light'
  const text = isLight ? beoflowColors.beoBlack : beoflowColors.beoWhite
  const highlight = isLight ? '#F5F5F5' : `${beoflowColors.beoWhite}${opacity.interactive}`
  const noOutline = {
    outline: 'none !important',
  }
  return {
    fontSize: typography.fontSizeSm,
    padding: `${spacing.sm} ${spacing.lg}`,
    borderRadius: 0,
    transition: 'all 0.2s ease',
    color: text,
    ...noOutline,
    '&:hover': { backgroundColor: highlight, color: text, ...noOutline },
    '&.Mui-selected': {
      backgroundColor: highlight,
      color: text,
      ...noOutline,
      '&:hover': { backgroundColor: highlight, color: text, ...noOutline },
    },
    '&:focus': noOutline,
    '&.Mui-focusVisible': noOutline,
    '&:focus-visible': noOutline,
  }
}

/** The whole dropdown family, as one unit.
 *
 *  Kept together because splitting them is what produced the inconsistency in
 *  the first place: a paper from one mode with rows from another is unreadable,
 *  so anything that overrides the surface must override the rows in the same
 *  breath. `createChromeTheme` relies on that.
 *
 *  The offset lives on MuiMenu alone. A Menu should clear its anchor; a bare
 *  Popover (a picker, a hover card) positions itself and must not be nudged. */
export function menuComponents(mode: 'light' | 'dark') {
  return {
    MuiMenu: {
      styleOverrides: { paper: { ...menuSurface(mode), marginTop: spacing.xs } },
    },
    MuiPopover: {
      styleOverrides: { paper: menuSurface(mode) },
    },
    MuiMenuItem: {
      styleOverrides: { root: menuItemStyle(mode) },
    },
  }
}

// Components that are shared between light and dark modes
const sharedComponents = {
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        '&:focus': {
          outline: `2px solid ${beoflowColors.meanBlue}`,
          outlineOffset: '2px',
        },
        '&.Mui-focusVisible': {
          outline: `2px solid ${beoflowColors.meanBlue}`,
          outlineOffset: '2px',
          boxShadow: 'none !important',
          border: 'none',
        },
        '&:focus-visible': {
          outline: `2px solid ${beoflowColors.meanBlue}`,
          outlineOffset: '2px',
          boxShadow: 'none !important',
        },
      },
    },
  },

  MuiSelect: {
    styleOverrides: {
      root: {
        borderRadius: layout.inputCornerRadius,
      },
      select: {
        fontSize: layout.inputFontSize,
        paddingTop: '12px',
        paddingBottom: '12px',
        paddingLeft: '16px',
        paddingRight: '40px',
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box' as const,
      },
    },
  },

  MuiTable: {
    styleOverrides: {
      root: {
        fontFamily: typography.fontFamilyMonospace,
        borderCollapse: 'separate' as const,
        borderSpacing: 0,
      },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.medium,
        boxShadow: 'none',
      },
      standardSuccess: {
        backgroundColor: beoflowColors.tealBase,
        color: beoflowColors.beoWhite,
        border: `1px solid ${beoflowColors.tealDark}`,
        '& .MuiAlert-icon': { color: beoflowColors.beoWhite },
      },
      standardError: {
        backgroundColor: beoflowColors.redBase,
        color: beoflowColors.beoWhite,
        border: `1px solid ${beoflowColors.redDark}`,
        '& .MuiAlert-icon': { color: beoflowColors.beoWhite },
      },
      standardWarning: {
        backgroundColor: beoflowColors.yellowBase,
        color: beoflowColors.beoBlack,
        border: `1px solid ${beoflowColors.yellowDark}`,
        '& .MuiAlert-icon': { color: beoflowColors.beoBlack },
      },
      standardInfo: {
        backgroundColor: beoflowColors.meanBlue,
        color: beoflowColors.beoWhite,
        border: `1px solid ${beoflowColors.blueDark}`,
        '& .MuiAlert-icon': { color: beoflowColors.beoWhite },
      },
    },
  },

  MuiLink: {
    styleOverrides: {
      root: {
        color: beoflowColors.peakOrange,
        textDecoration: 'underline',
        textDecorationThickness: layout.borderThick,
        letterSpacing: typography.letterNormal,
        transition: 'all 0.2s ease',
        '&:hover': {
          textDecoration: 'underline',
          textDecorationThickness: layout.borderThick,
          transform: 'translateY(-1px)',
        },
      },
    },
  },

  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        border: 'none',
        boxShadow: 'none',
        outline: 'none !important',
      },
    },
  },

  MuiCssBaseline: {
    styleOverrides: {
      '*': {
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        textRendering: 'optimizeLegibility',
      },
      // On HTML, not body. `document.scrollingElement` is <html> here, and
      // overscroll-behavior on <body> does NOT propagate to the viewport the
      // way overflow does — setting it there changed nothing at all.
      //
      // No elastic rubber-band at the ends of the page. The bounce moves the
      // whole compositing layer, so anything pinned to the viewport — the
      // sticky library rail, the navbar — rides it, which is what reads as the
      // panel "bumping" when the content beside it hits the end of its scroll.
      // Fixed positioning does not escape this either; only stopping the
      // overscroll does. Wheel/trackpad scrolling itself is unaffected.
      html: {
        overscrollBehaviorY: 'none',
      },
      body: {
        overscrollBehaviorY: 'none',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        textRendering: 'optimizeLegibility',
      },
      '#root': {
        width: '100%',
        minHeight: '100vh',
      },
      '.data, .metric, .number, code, kbd, samp, pre': {
        fontFamily: typography.fontFamilyMonospace,
        textTransform: 'uppercase',
      },
      'input[type="number"], input[type="tel"], input[type="date"], input[type="time"]': {
        fontFamily: typography.fontFamilyMonospace,
        textTransform: 'uppercase',
      },
    },
  },

  MuiTypography: {
    styleOverrides: {
      root: {
        '&.data-value, &.metric-value, &.number-display': {
          fontFamily: typography.fontFamilyMonospace,
          textTransform: 'uppercase',
        },
      },
    },
  },
}

// ============================================================================
// DARK MODE COMPONENTS
// ============================================================================
// Per-variant accent colors come from the generated button spec (buttonSpec.variants.*),
// not re-picked by hand — the web glass aesthetic (opacity, dark-on-light text) is layered
// on top. Keys map web button variants → the Drafted spec's variant names (primary → signal).
const btn = buttonSpec.variants

const darkComponents = {
  ...menuComponents('dark'),

  ...sharedComponents,

  MuiButton: {
    defaultProps: {
      disableRipple: true,
      disableFocusRipple: true,
    },
    variants: [
      {
        props: { variant: 'glassSecondary' as const },
        style: {
          backgroundColor: `${btn.secondary.tint}26`,
          border: `1px solid ${btn.secondary.tint}`,
          color: btn.secondary.tint,
          '&:hover': { backgroundColor: `${btn.secondary.tint}40` },
        },
      },
      {
        props: { variant: 'glassLight' as const },
        style: {
          backgroundColor: `${btn.light.tint}1A`,
          color: btn.light.label,
          border: `1px solid ${btn.light.tint}26`,
          '&:hover': { backgroundColor: `${btn.light.tint}26` },
        },
      },
      {
        props: { variant: 'glassDark' as const },
        style: {
          backgroundColor: `${btn.dark.tint}CC`,
          color: beoflowColors.beoWhite, // web glass keeps light text on the dark fill
          border: `1px solid ${beoflowColors.beoWhite}1A`,
          '&:hover': { backgroundColor: btn.dark.tint },
        },
      },
      {
        props: { variant: 'glassOutline' as const },
        style: {
          backgroundColor: `${btn.outline.tint}08`,
          border: `1px solid ${btn.outline.tint}26`,
          color: btn.outline.label,
          '&:hover': { backgroundColor: `${btn.outline.tint}1A` },
        },
      },
      {
        props: { variant: 'glassOutlineSecondary' as const },
        style: {
          backgroundColor: `${btn.outlineSecondary.tint}0D`,
          border: `1px solid ${btn.outlineSecondary.tint}`,
          color: btn.outlineSecondary.tint,
          '&:hover': { backgroundColor: `${btn.outlineSecondary.tint}1A` },
        },
      },
    ],
    styleOverrides: {
      root: {
        textTransform: 'uppercase',
        fontFamily: typography.fontFamilyMonospace,
        fontWeight: typography.fontWeightMedium,
        fontSize: typography.fontSizeSm,
        lineHeight: 1.6,
        letterSpacing: typography.letterButton,
        padding: '0.5rem 1rem',
        borderRadius: borderRadius.round,
        border: `2px solid ${beoflowColors.beoWhite}`,
        boxShadow: 'none',
        transition: 'background-color 0.15s ease, opacity 0.15s ease',
        // Native-flat press to mirror iOS (.borderedProminent): no brutalist lift/hard shadow.
        // Hover darkens the fill (variant-level); active dims slightly like an iOS button press.
        '&:hover': { boxShadow: 'none', transform: 'none' },
        '&:active': { boxShadow: 'none', transform: 'none', opacity: 0.9 },
        '&:focus': { outline: `2px solid ${beoflowColors.meanBlue}`, outlineOffset: '2px' },
        '&.Mui-focusVisible': { outline: `2px solid ${beoflowColors.meanBlue}`, outlineOffset: '2px', boxShadow: 'none' },
        '&.Mui-selected': { backgroundColor: 'inherit', boxShadow: 'none', '&:focus': { outline: `2px solid ${beoflowColors.meanBlue}`, outlineOffset: '2px' } },
      },
      sizeSmall: { padding: '0.375rem 0.75rem', fontSize: typography.fontSizeXs },
      sizeLarge: { padding: '0.625rem 1.25rem', fontSize: typography.fontSizeLg },
      contained: { boxShadow: 'none' },
      containedPrimary: {
        backgroundColor: btn.signal.tint,
        color: btn.signal.label,
        border: `1px solid ${btn.signal.tint}`,
        '&:hover': { backgroundColor: `${btn.signal.tint}E6` },
      },
      containedSecondary: {
        backgroundColor: btn.secondary.tint,
        color: btn.secondary.label,
        border: `1px solid ${btn.secondary.tint}`,
        '&:hover': { backgroundColor: `${btn.secondary.tint}E6` },
      },
      outlined: {
        backgroundColor: `${btn.outline.tint}08`,
        border: `1px solid ${btn.outline.tint}26`,
        color: btn.outline.label,
        '&:hover': { backgroundColor: `${btn.outline.tint}1A` },
      },
      outlinedPrimary: {
        backgroundColor: `${btn.outline.tint}08`,
        border: `1px solid ${btn.outline.tint}26`,
        color: btn.outline.label,
        '&:hover': { backgroundColor: `${btn.outline.tint}1A` },
      },
      outlinedSecondary: {
        backgroundColor: `${btn.outlineSecondary.tint}0D`,
        border: `1px solid ${btn.outlineSecondary.tint}`,
        color: btn.outlineSecondary.tint,
        '&:hover': { backgroundColor: `${btn.outlineSecondary.tint}1A` },
      },
      text: {
        backgroundColor: 'transparent',
        border: 'none',
        textDecoration: 'underline',
        textDecorationThickness: '1px',
        textUnderlineOffset: '2px',
        color: btn.text.label,
        '&:hover': { backgroundColor: 'transparent', textDecorationThickness: '2px' },
      },
      textPrimary: {
        backgroundColor: 'transparent',
        border: 'none',
        textDecoration: 'underline',
        textDecorationThickness: '1px',
        textUnderlineOffset: '2px',
        color: btn.textPrimary.label,
        '&:hover': { backgroundColor: 'transparent', textDecorationThickness: '2px' },
      },
      textSecondary: {
        backgroundColor: 'transparent',
        border: 'none',
        textDecoration: 'underline',
        textDecorationThickness: '1px',
        textUnderlineOffset: '2px',
        color: btn.textSecondary.label,
        '&:hover': { backgroundColor: 'transparent', textDecorationThickness: '2px' },
      },
    },
  },

  MuiButtonGroup: {
    styleOverrides: {
      root: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderRadius: borderRadius.round,
        padding: '2px',
        border: '1px solid rgba(248, 246, 244, 0.2)',
      },
      grouped: {
        border: 'none',
        '&:not(:last-of-type)': { borderRight: 'none' },
        '&.MuiButton-outlined': {
          border: 'none',
          backgroundColor: 'transparent',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)', border: 'none' },
        },
        '&.MuiButton-contained': {
          backgroundColor: beoflowColors.meanBlue,
          color: beoflowColors.beoWhite,
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: 'none',
          boxShadow: 'none',
          '&:hover': { backgroundColor: beoflowColors.meanBlue, color: beoflowColors.beoWhite, boxShadow: 'none' },
        },
      },
    },
  },

  MuiCard: {
    variants: [
      {
        props: { variant: 'data' as const },
        style: {
          backgroundColor: beoflowColors.graphGreyDark,
          border: 'none',
          borderRadius: px(cardSpec.cornerRadiusToken),
          backgroundImage: 'none',
          boxShadow: 'none',
          padding: px(cardSpec.paddingToken),
          transition: 'border-color 0.2s ease, background-color 0.2s ease',
          // iOS cards are flat containers — no brutalist lift/hard shadow on hover.
          '&:hover': { transform: 'none', boxShadow: 'none' },
        },
      },
    ],
    styleOverrides: {
      // Card spec v2 (Drafted components/06-card): fill-only surface, NO border on the
      // default card. Dark fill = beoWhite 5%; radius/padding from the generated spec.
      root: {
        borderRadius: px(cardSpec.cornerRadiusToken),
        border: 'none',
        backgroundColor: `${beoflowColors.beoWhite}0D`,
        backgroundImage: 'none',
        boxShadow: 'none',
        padding: px(cardSpec.paddingToken),
        transition: 'all 0.2s ease',
      },
    },
  },

  MuiPaper: {
    variants: [
      { props: { variant: 'glass' as const }, style: glassCard },
      { props: { variant: 'glassSmall' as const }, style: glassCardSmall },
      { props: { variant: 'glassInfo' as const }, style: glassInfoBox },
    ],
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backgroundColor: `${beoflowColors.beoWhite}08`,
        border: `1px solid ${beoflowColors.beoWhite}26`,
      },
      rounded: { borderRadius: borderRadius.medium },
      elevation3: { boxShadow: shadows.medium },
    },
  },

  MuiTextField: {
    defaultProps: { InputLabelProps: { shrink: true } },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          fontFamily: typography.fontFamilyMonospace,
          borderRadius: layout.inputCornerRadius,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          // Resting border width from the generated text-field spec; rgba tints + focus ring are the web aesthetic.
          '& fieldset': { borderColor: 'rgba(248, 246, 244, 0.3)', borderWidth: px(textFieldSpec.border.widthToken) },
          '&:hover fieldset': { borderColor: 'rgba(248, 246, 244, 0.5)' },
          '&.Mui-focused fieldset': { borderColor: beoflowColors.meanBlue, borderWidth: '2px', boxShadow: 'none' },
        },
        '& .MuiInputBase-input': {
          fontFamily: typography.fontFamilyMonospace,
          fontSize: layout.inputFontSize,
          padding: '12px 16px',
        },
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: layout.inputCornerRadius,
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(248, 246, 244, 0.3)', borderWidth: px(textFieldSpec.border.widthToken) },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(248, 246, 244, 0.5)' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: beoflowColors.meanBlue, borderWidth: '2px' },
      },
      input: { padding: '14px 16px' },
    },
  },

  MuiInput: {
    styleOverrides: {
      root: {
        fontFamily: typography.fontFamilyMonospace,
        fontSize: layout.inputFontSize,
        fontWeight: typography.fontWeightNormal,
        lineHeight: 1.6,
        '&::before': { borderBottomColor: beoflowColors.beoWhite, borderBottomWidth: layout.borderThick },
        '&:hover:not(.Mui-disabled)::before': { borderBottomColor: beoflowColors.beoWhite, borderBottomWidth: layout.borderThick },
        '&.Mui-focused::after': { borderBottomColor: beoflowColors.meanBlue, borderBottomWidth: layout.borderThick },
      },
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontSize: layout.inputFontSize,
        fontWeight: typography.fontWeightNormal,
        color: beoflowColors.beoWhite,
        '&.Mui-focused': { color: beoflowColors.meanBlue },
      },
    },
  },

  // Tables carry NO surface of their own. Both blocks below used a descendant
  // selector (`& .MuiTableCell-root`), which outranks a component's own `sx`,
  // so a caller could not opt out of any of this:
  //
  //   head: beoBlack fill + a 2px SOLID beoWhite underline
  //   body: solid graphGreyDark rules, and a graphGreyDark hover
  //
  // That is an elevated slab and three opaque greys sitting inside whatever
  // container the table is already in, which is why every table read heavier
  // than the page around it. Values now come from the generated specs:
  // cardSpec says grouping is a fill step plus whitespace, and listRowSpec puts
  // the neutral rule at beoWhite 6% and the row surface step at 8%.
  MuiTableHead: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-root': {
          fontFamily: typography.fontFamilyMonospace,
          fontSize: typography.fontSizeTiny,
          fontWeight: typography.fontWeightSemibold,
          textTransform: 'uppercase',
          letterSpacing: typography.letterLabel,
          padding: `${spacing.md} ${spacing.lg}`,
          backgroundColor: 'transparent',
          color: beoflowColors.graphGreyLight,
          // One step stronger than a row rule, so the header still reads as a
          // header without being a slab.
          borderBottom: `${layout.borderDefault} solid ${beoflowColors.beoWhite}${opacity.border}`,
        },
      },
    },
  },

  MuiTableBody: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-root': {
          fontFamily: typography.fontFamilyMonospace,
          fontSize: typography.fontSizeSm,
          padding: `${spacing.md} ${spacing.lg}`,
          borderBottom: `${layout.borderDefault} solid ${beoflowColors.beoWhite}0F`,
        },
        '& .MuiTableRow-root:hover': {
          backgroundColor: `${beoflowColors.beoWhite}${opacity.separator}`,
        },
      },
    },
  },

  MuiChip: {
    // Border width comes from the generated chip spec (chipSpec.tokens.borderWidth); the glass
    // fill + accent-opacity border color are the web-only rendering of the iOS capsule chip.
    variants: [
      {
        props: { variant: 'glass' as const },
        style: { backgroundColor: `${beoflowColors.beoWhite}08`, border: `${px(chipSpec.tokens.borderWidth)} solid ${beoflowColors.beoWhite}26` },
      },
      // `tag` is for a NAME (a group, a linked plan), not a status.
      // The root chip is a mono UPPERCASE capsule, which is right for ACTIVE or
      // STRENGTH and wrong for a proper noun: "NOVUS LD" is a different thing
      // from Novus LD. So tag opts out of uppercase, mono and the capsule, and
      // carries no fill: it is a bordered value, and the border is the palette
      // divider so it works in both modes.
      // It lives HERE and not in a component, because a group tag and a plan
      // pill hand-rolled separately is exactly how they drifted to different
      // sizes in adjacent table columns.
      {
        props: { variant: 'tag' as const },
        style: {
          fontFamily: typography.fontFamilySansSerif,
          textTransform: 'none' as const,
          letterSpacing: 'normal',
          fontSize: typography.fontSizeXs,
          borderRadius: borderRadius.small,
          height: 'auto',
          backgroundColor: 'transparent',
          border: `${px(chipSpec.tokens.borderWidth)} solid ${beoflowColors.beoWhite}${opacity.separator}`,
          '& .MuiChip-label': { paddingLeft: '9px', paddingRight: '9px', paddingTop: '3px', paddingBottom: '3px' },
          '& .MuiChip-icon': { fontSize: 12, marginLeft: '7px', marginRight: '-3px' },
        },
      },
    ],
    styleOverrides: {
      root: {
        // iOS chip is a capsule with IBM Plex Mono UPPERCASE label (not a rounded-rect).
        fontFamily: typography.fontFamilyMonospace,
        borderRadius: borderRadius.round,
        textTransform: 'uppercase',
        letterSpacing: typography.letterLabel,
        fontWeight: typography.fontWeightMedium,
        // iOS chip is compact: 11px label, 24px tall, tight padding (MUI default 32px/13px was too large).
        fontSize: px(chipSpec.font.size),
        height: 24,
        border: `${px(chipSpec.tokens.borderWidth)} solid ${beoflowColors.beoWhite}26`,
        backgroundColor: `${beoflowColors.beoWhite}08`,
      },
      label: { paddingLeft: '10px', paddingRight: '10px' },
      sizeSmall: { fontSize: typography.fontSizeTiny, height: 22 },
    },
  },

  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: beoflowColors.beoBlack,
        borderRight: `${layout.borderThick} solid ${beoflowColors.beoWhite}`,
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: typography.fontSizeSm,
        background: glass.ultraThin.background,
        backdropFilter: `blur(${glass.ultraThin.backdropBlur})`,
        WebkitBackdropFilter: `blur(${glass.ultraThin.backdropBlur})`,
        border: glass.ultraThin.border,
        borderRadius: borderRadius.small,
        padding: `${spacing.sm} ${spacing.md}`,
        boxShadow: glass.ultraThin.shadow,
        color: beoflowColors.beoWhite,
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
      },
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.small,
        height: 8,
        border: `1px solid ${beoflowColors.beoWhite}26`,
        backgroundColor: `${beoflowColors.beoWhite}08`,
      },
      bar: { borderRadius: borderRadius.small },
    },
  },

  MuiBadge: {
    styleOverrides: {
      badge: {
        fontFamily: typography.fontFamilyMonospace,
        fontSize: '0.75em',
        fontWeight: typography.fontWeightSemibold,
        borderRadius: borderRadius.small,
        border: `1px solid ${beoflowColors.beoWhite}26`,
        padding: `${spacing.xs} ${spacing.sm}`,
      },
    },
  },

  MuiSwitch: {
    styleOverrides: {
      root: {
        '& .MuiSwitch-switchBase.Mui-checked': {
          color: beoflowColors.beoWhite,
          '& + .MuiSwitch-track': { backgroundColor: beoflowColors.meanBlue, opacity: 1 },
        },
        '& .MuiSwitch-track': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          opacity: 1,
          borderRadius: borderRadius.medium,
        },
        '& .MuiSwitch-thumb': { backgroundColor: beoflowColors.beoWhite, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)' },
      },
    },
  },

  MuiCheckbox: {
    styleOverrides: {
      root: {
        ...glassToggleControl,
        padding: '9px',
        borderRadius: '8px',
        '&.Mui-checked .MuiSvgIcon-root': { borderRadius: '8px' },
        '& .MuiSvgIcon-root': { borderRadius: '8px' },
      },
    },
  },

  MuiRadio: {
    styleOverrides: {
      root: {
        ...glassToggleControl,
        '& .MuiSvgIcon-root': { borderRadius: '50%' },
      },
    },
  },

  MuiSlider: {
    styleOverrides: {
      root: {
        color: beoflowColors.meanBlue,
        '& .MuiSlider-thumb': {
          backgroundColor: beoflowColors.beoWhite,
          border: `2px solid ${beoflowColors.meanBlue}`,
          width: 20, height: 20,
          '&:hover': { boxShadow: '0 0 0 8px rgba(74, 155, 184, 0.16)' },
        },
        '& .MuiSlider-track': { backgroundColor: beoflowColors.meanBlue, border: 'none' },
        '& .MuiSlider-rail': { backgroundColor: 'rgba(248, 246, 244, 0.2)' },
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      root: { borderBottom: `${layout.borderThick} solid ${beoflowColors.beoWhite}` },
      indicator: { backgroundColor: beoflowColors.beoWhite, height: '4px' },
    },
  },

  MuiTab: {
    defaultProps: { disableFocusRipple: true },
    styleOverrides: {
      root: {
        fontFamily: typography.fontFamilySansSerif,
        textTransform: 'uppercase',
        fontWeight: typography.fontWeightNormal,
        fontSize: typography.fontSizeSm,
        padding: `${spacing.md} ${spacing.lg}`,
        borderRadius: 0,
        transition: 'all 0.2s ease',
        border: 'none !important',
        boxShadow: 'none !important',
        outline: 'none !important',
        color: beoflowColors.beoWhite,
        '&:hover': { border: 'none !important', boxShadow: 'none !important', outline: 'none !important' },
        '&.Mui-selected': { fontWeight: typography.fontWeightSemibold, color: beoflowColors.beoWhite },
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      root: {
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        },
      },
      paper: {
        ...createGlassOverlay(glass.regular, { borderRadius: borderRadius.sheet }),
        // iOS presents a sheet as a shape lifted off the backdrop, not as a
        // stroked box: no outline, and depth carried entirely by the shadow.
        // HIG owns modal structure (theme/AGENTS.md), so this is theme-wide.
        border: 'none',
        boxShadow: glass.softShadowLarge,
        // A full-screen dialog has no backdrop to lift off — it IS the screen.
        // MUI's own paperFullScreen sets radius 0; the rule above outranks it,
        // so restate it or every mobile sheet gets rounded corners and a shadow
        // against the viewport edge.
        '&.MuiDialog-paperFullScreen': { borderRadius: 0, boxShadow: 'none' },
        '& .MuiDialogTitle-root': { fontFamily: typography.fontFamilyMonospace },
      },
    },
  },

  MuiAppBar: {
    styleOverrides: {
      root: {
        ...createGlassOverlay(glass.thin),
        borderBottom: `2px solid ${beoflowColors.peakOrange}`,
        boxShadow: glass.softShadow,
      },
    },
  },

  MuiToolbar: {
    styleOverrides: {
      root: createGlassOverlay(glass.thin),
    },
  },

  MuiDivider: {
    variants: [
      { props: { variant: 'glass' as const }, style: { backgroundColor: `${beoflowColors.beoWhite}33` } },
    ],
  },

  MuiAccordion: {
    variants: [
      {
        props: { variant: 'glass' as const },
        style: {
          backgroundColor: `${beoflowColors.beoWhite}08`,
          border: `1px solid ${beoflowColors.beoWhite}26`,
          borderRadius: '8px',
          '&:before': { display: 'none' },
        },
      },
    ],
    styleOverrides: {
      root: {
        borderRadius: borderRadius.small,
        border: `1px solid ${beoflowColors.beoWhite}26`,
        backgroundColor: `${beoflowColors.beoWhite}05`,
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: `${spacing.sm} 0` },
        '&:first-of-type': { borderTopLeftRadius: borderRadius.small, borderTopRightRadius: borderRadius.small },
        '&:last-of-type': { borderBottomLeftRadius: borderRadius.small, borderBottomRightRadius: borderRadius.small },
      },
    },
  },
}

// ============================================================================
// LIGHT MODE COMPONENTS
// ============================================================================
const lightComponents = {
  ...menuComponents('light'),

  ...sharedComponents,

  MuiButton: {
    defaultProps: {
      disableRipple: true,
      disableFocusRipple: true,
    },
    variants: [
      {
        props: { variant: 'glassSecondary' as const },
        style: {
          backgroundColor: `${beoflowColors.meanBlue}15`,
          border: `1px solid ${beoflowColors.meanBlue}`,
          color: beoflowColors.meanBlue,
          '&:hover': { backgroundColor: `${beoflowColors.meanBlue}26` },
        },
      },
      {
        props: { variant: 'glassLight' as const },
        style: {
          backgroundColor: `${beoflowColors.beoBlack}08`,
          color: beoflowColors.beoBlack,
          border: `1px solid #E0E0E0`,
          '&:hover': { backgroundColor: `${beoflowColors.beoBlack}12` },
        },
      },
      {
        props: { variant: 'glassDark' as const },
        style: {
          backgroundColor: beoflowColors.beoBlack,
          color: beoflowColors.beoWhite,
          border: `1px solid ${beoflowColors.beoBlack}`,
          '&:hover': { backgroundColor: beoflowColors.beoBlacker },
        },
      },
      {
        props: { variant: 'glassOutline' as const },
        style: {
          backgroundColor: 'transparent',
          border: `1px solid #E0E0E0`,
          color: beoflowColors.beoBlack,
          '&:hover': { backgroundColor: '#F5F5F5' },
        },
      },
      {
        props: { variant: 'glassOutlineSecondary' as const },
        style: {
          backgroundColor: `${beoflowColors.meanBlue}08`,
          border: `1px solid ${beoflowColors.meanBlue}`,
          color: beoflowColors.meanBlue,
          '&:hover': { backgroundColor: `${beoflowColors.meanBlue}15` },
        },
      },
    ],
    styleOverrides: {
      root: {
        textTransform: 'uppercase',
        fontFamily: typography.fontFamilyMonospace,
        fontWeight: typography.fontWeightMedium,
        fontSize: typography.fontSizeSm,
        lineHeight: 1.6,
        letterSpacing: typography.letterButton,
        padding: '0.5rem 1rem',
        borderRadius: borderRadius.round,
        border: `2px solid ${beoflowColors.beoBlack}`,
        boxShadow: 'none',
        transition: 'all 0.2s ease',
        '&:hover': { boxShadow: shadows.small, transform: 'translateY(-2px)' },
        '&:active': { boxShadow: shadows.pressed, transform: 'translateY(1px)' },
        '&:focus': { outline: `2px solid ${beoflowColors.meanBlue}`, outlineOffset: '2px' },
        '&.Mui-focusVisible': { outline: `2px solid ${beoflowColors.meanBlue}`, outlineOffset: '2px', boxShadow: 'none' },
        '&.Mui-selected': { backgroundColor: 'inherit', boxShadow: 'none', '&:focus': { outline: `2px solid ${beoflowColors.meanBlue}`, outlineOffset: '2px' } },
      },
      sizeSmall: { padding: '0.375rem 0.75rem', fontSize: typography.fontSizeXs },
      sizeLarge: { padding: '0.625rem 1.25rem', fontSize: typography.fontSizeLg },
      contained: { boxShadow: 'none' },
      containedPrimary: {
        backgroundColor: beoflowColors.beoBlack,
        color: beoflowColors.beoWhite,
        border: `1px solid ${beoflowColors.beoBlack}`,
        '&:hover': { backgroundColor: beoflowColors.beoBlacker },
      },
      containedSecondary: {
        backgroundColor: beoflowColors.meanBlue,
        color: beoflowColors.beoWhite,
        border: `1px solid ${beoflowColors.meanBlue}`,
        '&:hover': { backgroundColor: beoflowColors.blueDark },
      },
      outlined: {
        backgroundColor: 'transparent',
        border: `1px solid ${beoflowColors.beoBlack}`,
        color: beoflowColors.beoBlack,
        '&:hover': { backgroundColor: '#F5F5F5' },
      },
      outlinedPrimary: {
        backgroundColor: 'transparent',
        border: `1px solid ${beoflowColors.beoBlack}`,
        color: beoflowColors.beoBlack,
        '&:hover': { backgroundColor: '#F5F5F5' },
      },
      outlinedSecondary: {
        backgroundColor: `${beoflowColors.meanBlue}08`,
        border: `1px solid ${beoflowColors.meanBlue}`,
        color: beoflowColors.meanBlue,
        '&:hover': { backgroundColor: `${beoflowColors.meanBlue}15` },
      },
      text: {
        backgroundColor: 'transparent',
        border: 'none',
        textDecoration: 'underline',
        textDecorationThickness: '1px',
        textUnderlineOffset: '2px',
        color: beoflowColors.beoBlack,
        '&:hover': { backgroundColor: 'transparent', textDecorationThickness: '2px' },
      },
      textPrimary: {
        backgroundColor: 'transparent',
        border: 'none',
        textDecoration: 'underline',
        textDecorationThickness: '1px',
        textUnderlineOffset: '2px',
        color: beoflowColors.meanBlue,
        '&:hover': { backgroundColor: 'transparent', textDecorationThickness: '2px' },
      },
      textSecondary: {
        backgroundColor: 'transparent',
        border: 'none',
        textDecoration: 'underline',
        textDecorationThickness: '1px',
        textUnderlineOffset: '2px',
        color: beoflowColors.peakOrange,
        '&:hover': { backgroundColor: 'transparent', textDecorationThickness: '2px' },
      },
    },
  },

  MuiButtonGroup: {
    styleOverrides: {
      root: {
        backgroundColor: '#F0F0F0',
        borderRadius: borderRadius.round,
        padding: '2px',
        border: '1px solid #E0E0E0',
      },
      grouped: {
        border: 'none',
        '&:not(:last-of-type)': { borderRight: 'none' },
        '&.MuiButton-outlined': {
          border: 'none',
          backgroundColor: 'transparent',
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)', border: 'none' },
        },
        '&.MuiButton-contained': {
          backgroundColor: beoflowColors.meanBlue,
          color: beoflowColors.beoWhite,
          border: 'none',
          boxShadow: 'none',
          '&:hover': { backgroundColor: beoflowColors.meanBlue, color: beoflowColors.beoWhite, boxShadow: 'none' },
        },
      },
    },
  },

  MuiCard: {
    variants: [
      {
        props: { variant: 'data' as const },
        style: {
          backgroundColor: '#F5F5F5',
          border: '1px solid #E0E0E0',
          borderRadius: borderRadius.card,
          backgroundImage: 'none',
          boxShadow: 'none',
          padding: spacing.cardPadding,
          transition: 'all 0.2s ease',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: shadows.medium },
        },
      },
    ],
    styleOverrides: {
      // Card spec v2, light canvas: paper-white fill + hairline shadow, no border.
      root: {
        borderRadius: borderRadius.card,
        border: 'none',
        backgroundColor: '#FFFFFF',
        backgroundImage: 'none',
        boxShadow: '0 1px 3px rgba(30,30,30,0.06)',
        padding: spacing.cardPadding,
        transition: 'all 0.2s ease',
      },
    },
  },

  MuiPaper: {
    variants: [
      // In light mode, glass variants resolve to simple light cards
      { props: { variant: 'glass' as const }, style: { backgroundColor: '#FFFFFF', border: '1px solid #E0E0E0', borderRadius: '12px' } },
      { props: { variant: 'glassSmall' as const }, style: { backgroundColor: '#FFFFFF', border: '1px solid #E0E0E0', borderRadius: '8px' } },
      { props: { variant: 'glassInfo' as const }, style: { backgroundColor: '#FFFFFF', border: '1px solid #E0E0E0', borderRadius: '8px' } },
    ],
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E0E0E0',
      },
      rounded: { borderRadius: borderRadius.medium },
      elevation3: { boxShadow: shadows.medium },
    },
  },

  MuiTextField: {
    defaultProps: { InputLabelProps: { shrink: true } },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          fontFamily: typography.fontFamilyMonospace,
          borderRadius: layout.inputCornerRadius,
          backgroundColor: '#FFFFFF',
          '& fieldset': { borderColor: '#DCDCDC', borderWidth: '1px' },
          '&:hover fieldset': { borderColor: '#AAAAAA' },
          '&.Mui-focused fieldset': { borderColor: beoflowColors.meanBlue, borderWidth: '2px', boxShadow: 'none' },
        },
        '& .MuiInputBase-input': {
          fontFamily: typography.fontFamilyMonospace,
          fontSize: layout.inputFontSize,
          padding: '12px 16px',
        },
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: layout.inputCornerRadius,
        backgroundColor: '#FFFFFF',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#DCDCDC', borderWidth: '1px' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#AAAAAA' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: beoflowColors.meanBlue, borderWidth: '2px' },
      },
      input: { padding: '14px 16px' },
    },
  },

  MuiInput: {
    styleOverrides: {
      root: {
        fontFamily: typography.fontFamilyMonospace,
        fontSize: layout.inputFontSize,
        fontWeight: typography.fontWeightNormal,
        lineHeight: 1.6,
        '&::before': { borderBottomColor: beoflowColors.beoBlack, borderBottomWidth: layout.borderThick },
        '&:hover:not(.Mui-disabled)::before': { borderBottomColor: beoflowColors.beoBlack, borderBottomWidth: layout.borderThick },
        '&.Mui-focused::after': { borderBottomColor: beoflowColors.meanBlue, borderBottomWidth: layout.borderThick },
      },
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontSize: layout.inputFontSize,
        fontWeight: typography.fontWeightNormal,
        color: beoflowColors.graphGreyDark,
        '&.Mui-focused': { color: beoflowColors.meanBlue },
      },
    },
  },

  MuiTableHead: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-root': {
          fontFamily: typography.fontFamilyMonospace,
          fontSize: typography.fontSizeTiny,
          fontWeight: typography.fontWeightSemibold,
          textTransform: 'uppercase',
          letterSpacing: typography.letterLabel,
          padding: `${spacing.md} ${spacing.lg}`,
          // Light mirror of the dark fix: no fill, no 2px slab. This is the
          // DEFAULT mode on web (ThemeContext returns 'light' unless the mobile
          // WebView asks for dark), so it is the one most coaches see.
          backgroundColor: 'transparent',
          color: 'rgba(30, 30, 30, 0.6)',
          borderBottom: `${layout.borderDefault} solid rgba(30, 30, 30, 0.24)`,
        },
      },
    },
  },

  MuiTableBody: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-root': {
          fontFamily: typography.fontFamilyMonospace,
          fontSize: typography.fontSizeSm,
          padding: `${spacing.md} ${spacing.lg}`,
          borderBottom: `${layout.borderDefault} solid #E0E0E0`,
        },
        '& .MuiTableRow-root:hover': { backgroundColor: '#F5F5F5' },
      },
    },
  },

  MuiChip: {
    variants: [
      {
        props: { variant: 'glass' as const },
        style: { backgroundColor: '#F5F5F5', border: '1px solid #E0E0E0' },
      },
      // `tag` is for a NAME (a group, a linked plan), not a status.
      // The root chip is a mono UPPERCASE capsule, which is right for ACTIVE or
      // STRENGTH and wrong for a proper noun: "NOVUS LD" is a different thing
      // from Novus LD. So tag opts out of uppercase, mono and the capsule, and
      // carries no fill: it is a bordered value, and the border is the palette
      // divider so it works in both modes.
      // It lives HERE and not in a component, because a group tag and a plan
      // pill hand-rolled separately is exactly how they drifted to different
      // sizes in adjacent table columns.
      {
        props: { variant: 'tag' as const },
        style: {
          fontFamily: typography.fontFamilySansSerif,
          textTransform: 'none' as const,
          letterSpacing: 'normal',
          fontSize: typography.fontSizeXs,
          borderRadius: borderRadius.small,
          height: 'auto',
          backgroundColor: 'transparent',
          border: '1px solid #E0E0E0',
          '& .MuiChip-label': { paddingLeft: '9px', paddingRight: '9px', paddingTop: '3px', paddingBottom: '3px' },
          '& .MuiChip-icon': { fontSize: 12, marginLeft: '7px', marginRight: '-3px' },
        },
      },
    ],
    styleOverrides: {
      root: {
        fontFamily: typography.fontFamilyMonospace,
        borderRadius: borderRadius.medium,
        fontWeight: typography.fontWeightMedium,
        border: '1px solid #E0E0E0',
        backgroundColor: '#F5F5F5',
      },
      sizeSmall: { fontSize: typography.fontSizeTiny },
    },
  },

  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: '#FFFFFF',
        borderRight: '1px solid #E0E0E0',
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: typography.fontSizeSm,
        background: 'rgba(0, 0, 0, 0.87)',
        borderRadius: borderRadius.small,
        padding: `${spacing.sm} ${spacing.md}`,
        color: '#FFFFFF',
      },
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.small,
        height: 8,
        border: '1px solid #E0E0E0',
        backgroundColor: '#F0F0F0',
      },
      bar: { borderRadius: borderRadius.small },
    },
  },

  MuiBadge: {
    styleOverrides: {
      badge: {
        fontFamily: typography.fontFamilyMonospace,
        fontSize: '0.75em',
        fontWeight: typography.fontWeightSemibold,
        borderRadius: borderRadius.small,
        border: '1px solid #E0E0E0',
        padding: `${spacing.xs} ${spacing.sm}`,
      },
    },
  },

  MuiSwitch: {
    styleOverrides: {
      root: {
        '& .MuiSwitch-switchBase.Mui-checked': {
          color: '#FFFFFF',
          '& + .MuiSwitch-track': { backgroundColor: beoflowColors.meanBlue, opacity: 1 },
        },
        '& .MuiSwitch-track': {
          backgroundColor: '#E0E0E0',
          opacity: 1,
          borderRadius: borderRadius.medium,
        },
        '& .MuiSwitch-thumb': { backgroundColor: '#FFFFFF', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)' },
      },
    },
  },

  MuiCheckbox: {
    styleOverrides: {
      root: {
        color: beoflowColors.beoBlack,
        padding: '9px',
        borderRadius: '8px',
        '&.Mui-checked': { color: beoflowColors.meanBlue },
        '& .MuiSvgIcon-root': { borderRadius: '8px', fontSize: '24px' },
      },
    },
  },

  MuiRadio: {
    styleOverrides: {
      root: {
        color: beoflowColors.beoBlack,
        '&.Mui-checked': { color: beoflowColors.meanBlue },
        '& .MuiSvgIcon-root': { borderRadius: '50%' },
      },
    },
  },

  MuiSlider: {
    styleOverrides: {
      root: {
        color: beoflowColors.meanBlue,
        '& .MuiSlider-thumb': {
          backgroundColor: '#FFFFFF',
          border: `2px solid ${beoflowColors.meanBlue}`,
          width: 20, height: 20,
          '&:hover': { boxShadow: '0 0 0 8px rgba(74, 155, 184, 0.16)' },
        },
        '& .MuiSlider-track': { backgroundColor: beoflowColors.meanBlue, border: 'none' },
        '& .MuiSlider-rail': { backgroundColor: '#E0E0E0' },
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      root: { borderBottom: `${layout.borderThick} solid ${beoflowColors.beoBlack}` },
      indicator: { backgroundColor: beoflowColors.beoBlack, height: '4px' },
    },
  },

  MuiTab: {
    defaultProps: { disableFocusRipple: true },
    styleOverrides: {
      root: {
        fontFamily: typography.fontFamilySansSerif,
        textTransform: 'uppercase',
        fontWeight: typography.fontWeightNormal,
        fontSize: typography.fontSizeSm,
        padding: `${spacing.md} ${spacing.lg}`,
        borderRadius: 0,
        transition: 'all 0.2s ease',
        border: 'none !important',
        boxShadow: 'none !important',
        outline: 'none !important',
        color: beoflowColors.beoBlack,
        '&:hover': { border: 'none !important', boxShadow: 'none !important', outline: 'none !important' },
        '&.Mui-selected': { fontWeight: typography.fontWeightSemibold, color: beoflowColors.beoBlack },
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      root: {
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        },
      },
      paper: {
        backgroundColor: '#FFFFFF',
        // Matches the dark theme above: no stroke, sheet radius, depth from the
        // shadow. Light needs a softer shadow than dark to read as lifted.
        border: 'none',
        borderRadius: borderRadius.sheet,
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.18)',
        backgroundImage: 'none',
        // See the dark block: full-screen dialogs must keep MUI's square edge.
        '&.MuiDialog-paperFullScreen': { borderRadius: 0, boxShadow: 'none' },
        '& .MuiDialogTitle-root': { fontFamily: typography.fontFamilyMonospace },
      },
    },
  },

  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: '#FFFFFF',
        color: beoflowColors.beoBlack,
        borderBottom: `2px solid ${beoflowColors.peakOrange}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        backgroundImage: 'none',
      },
    },
  },

  MuiToolbar: {
    styleOverrides: {
      root: {
        backgroundColor: '#FFFFFF',
      },
    },
  },

  MuiDivider: {
    variants: [
      { props: { variant: 'glass' as const }, style: { backgroundColor: '#E0E0E0' } },
    ],
  },

  MuiAccordion: {
    variants: [
      {
        props: { variant: 'glass' as const },
        style: {
          backgroundColor: '#FFFFFF',
          border: '1px solid #E0E0E0',
          borderRadius: '8px',
          '&:before': { display: 'none' },
        },
      },
    ],
    styleOverrides: {
      root: {
        borderRadius: borderRadius.small,
        border: '1px solid #E0E0E0',
        backgroundColor: '#FFFFFF',
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: `${spacing.sm} 0` },
        '&:first-of-type': { borderTopLeftRadius: borderRadius.small, borderTopRightRadius: borderRadius.small },
        '&:last-of-type': { borderBottomLeftRadius: borderRadius.small, borderBottomRightRadius: borderRadius.small },
      },
    },
  },
}

// ============================================================================
// PALETTE DEFINITIONS
// ============================================================================
const darkPalette = {
  mode: 'dark' as const,
  primary: {
    main: beoflowColors.meanBlue,
    light: beoflowColors.blueLight,
    dark: beoflowColors.blueDark,
    contrastText: beoflowColors.beoWhite,
  },
  secondary: {
    main: beoflowColors.peakOrange,
    light: beoflowColors.orangeLight,
    dark: beoflowColors.orangeDark,
    contrastText: beoflowColors.beoWhite,
  },
  error: {
    main: beoflowColors.redBase,
    light: beoflowColors.redLight,
    dark: beoflowColors.redDark,
    contrastText: beoflowColors.beoWhite,
  },
  warning: {
    main: beoflowColors.yellowBase,
    light: beoflowColors.yellowLight,
    dark: beoflowColors.yellowDark,
    contrastText: beoflowColors.beoBlack,
  },
  info: {
    main: beoflowColors.meanBlue,
    light: beoflowColors.blueLight,
    dark: beoflowColors.blueDark,
    contrastText: beoflowColors.beoWhite,
  },
  success: {
    main: beoflowColors.tealBase,
    light: beoflowColors.tealLight,
    dark: beoflowColors.tealDark,
    contrastText: beoflowColors.beoWhite,
  },
  background: {
    // Design system: beoBlacker #121212 = base canvas; beoBlack #1E1E1E = elevated surface.
    default: beoflowColors.beoBlacker,
    paper: beoflowColors.beoBlack,
  },
  text: {
    primary: beoflowColors.beoWhite,
    // Dimmed white (60%) per the design's secondary-text intent, not a bright grey.
    secondary: `${beoflowColors.beoWhite}${opacity.secondary}`,
  },
  // Subtle white hairline (8%) rather than a heavy solid grey.
  divider: `${beoflowColors.beoWhite}${opacity.separator}`,
  action: {
    active: beoflowColors.beoWhite,
    hover: 'rgba(248, 246, 244, 0.08)',
    selected: 'rgba(248, 246, 244, 0.16)',
    disabled: 'rgba(248, 246, 244, 0.26)',
    disabledBackground: 'rgba(248, 246, 244, 0.12)',
  },
  grey: {
    50: '#fafafa',
    100: '#f8f9fa',
    200: beoflowColors.beoWhite,
    300: beoflowColors.graphGreyLight,
    400: '#ced4da',
    500: '#adb5bd',
    600: beoflowColors.graphGreyDark,
    700: '#495057',
    800: '#343a40',
    900: beoflowColors.beoBlack,
  },
}

const lightPalette = {
  mode: 'light' as const,
  primary: {
    main: beoflowColors.meanBlue,
    light: beoflowColors.blueLight,
    dark: beoflowColors.blueDark,
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: beoflowColors.peakOrange,
    light: beoflowColors.orangeLight,
    dark: beoflowColors.orangeDark,
    contrastText: '#FFFFFF',
  },
  error: {
    main: beoflowColors.redBase,
    light: beoflowColors.redLight,
    dark: beoflowColors.redDark,
    contrastText: '#FFFFFF',
  },
  warning: {
    main: beoflowColors.yellowBase,
    light: beoflowColors.yellowLight,
    dark: beoflowColors.yellowDark,
    contrastText: beoflowColors.beoBlack,
  },
  info: {
    main: beoflowColors.meanBlue,
    light: beoflowColors.blueLight,
    dark: beoflowColors.blueDark,
    contrastText: '#FFFFFF',
  },
  success: {
    main: beoflowColors.tealBase,
    light: beoflowColors.tealLight,
    dark: beoflowColors.tealDark,
    contrastText: '#FFFFFF',
  },
  background: {
    default: beoflowColors.beoWhite,    // #F8F6F4 — warm white
    paper: '#FFFFFF',
  },
  text: {
    primary: beoflowColors.beoBlack,    // #000000
    secondary: beoflowColors.graphGreyDark, // #434647
  },
  divider: '#E0E0E0',
  action: {
    active: beoflowColors.beoBlack,
    hover: 'rgba(0, 0, 0, 0.04)',
    selected: 'rgba(0, 0, 0, 0.08)',
    disabled: 'rgba(0, 0, 0, 0.26)',
    disabledBackground: 'rgba(0, 0, 0, 0.12)',
  },
  grey: {
    50: '#fafafa',
    100: '#f8f9fa',
    200: '#F5F5F5',
    300: '#E0E0E0',
    400: '#ced4da',
    500: '#adb5bd',
    600: '#757575',
    700: '#495057',
    800: '#343a40',
    900: beoflowColors.beoBlack,
  },
}

// ============================================================================
// THEME FACTORY
// ============================================================================
export function createBeoflowTheme(mode: 'light' | 'dark') {
  const isLight = mode === 'light'
  return createTheme({
    palette: isLight ? lightPalette : darkPalette,
    typography: sharedTypography,
    spacing: 8,
    shape: sharedShape,
    breakpoints: sharedBreakpoints,
    shadows: sharedShadows,
    components: isLight ? lightComponents : darkComponents,
  })
}

/**
 * A dark chrome strip sitting over content that is NOT dark.
 *
 * The navbar is black in light mode, so it wraps itself in a dark theme to get
 * light-on-dark icons and text. That wrapper is a React context, not a DOM
 * boundary, so it also captured every dropdown opened from inside it --
 * including the ones pages inject through the navbar's `actions` slot. The
 * result was the same ellipsis menu rendering dark from the navbar and light
 * from the sidebar, which is a component looking like two components.
 *
 * `contentMode` is the mode of what the MENU will overlay, not of the chrome.
 * The navbar passes the app mode (the page behind it); the Lens header passes
 * 'dark' because its page is genuinely dark. Chrome stays dark either way.
 *
 * Note the whole menu family moves together: a light paper with dark-mode rows
 * is white text on white.
 */
export function createChromeTheme(contentMode: 'light' | 'dark') {
  return createTheme({
    palette: darkPalette,
    typography: sharedTypography,
    spacing: 8,
    shape: sharedShape,
    breakpoints: sharedBreakpoints,
    shadows: sharedShadows,
    components: { ...darkComponents, ...menuComponents(contentMode) },
  })
}

// Backward compatibility — default theme (light)
export const theme = createBeoflowTheme('light')

// Additional utilities are already exported above via 'export const'

// Export glass effects for use in components
export * from './glassEffects'
